<template>
  <div class="card mb-4">
    <div class="card-header">
      <div class="row flex-between-end">
        <div class="col-auto text-center">
          <h5 class="mb-0">Create API Key</h5>
        </div>
      </div>
    </div>
    <div class="card-body bg-body-tertiary">

      <!-- One-time token reveal: shown right after a key is created. The full
           token is returned by the API exactly once and can never be retrieved
           again, so the user must copy it now.
           v-show (not v-if) keeps the form below mounted across the
           create -> reveal -> dismiss cycle. -->
      <div v-show="createdToken">
        <div class="alert alert-success border-0 d-flex align-items-center" role="alert">
          <div class="bg-success me-3 icon-item"><span class="fas fa-check-circle text-white fs-6"></span></div>
          <p class="mb-0 flex-1">Your API key <span class="fw-bold">"{{ createdLabel }}"</span> was created.</p>
        </div>

        <p class="mb-2">Copy your key now - for security reasons it will <span class="fw-bold">never be shown again</span>. If you lose it, deactivate this key and create a new one.</p>

        <div class="input-group mb-3">
          <input :value="createdToken" class="form-control font-monospace" type="text" readonly @focus="$event.target.select()">
          <button @click="copyToken()" class="btn btn-primary" type="button">
            <span class="fas" :class="copied ? 'fa-check' : 'fa-copy'"></span>
            {{ copied ? 'Copied' : 'Copy' }}
          </button>
        </div>

        <p class="fs-10 text-700 mb-3">Use it as a Bearer token: <code>Authorization: Bearer {{ createdToken }}</code></p>

        <div class="row">
          <div class="col d-flex justify-content-end">
            <button @click="dismissReveal()" type="button" class="btn btn-secondary">Done</button>
          </div>
        </div>
      </div>

      <!-- Create form -->
      <div v-show="!createdToken">
        <p>API keys let your own scripts, bots and integrations access the Signum API. They are separate from your browser session - treat them like passwords and never share them.</p>

        <div v-if="submitError" class="alert alert-danger border-0 d-flex align-items-center" role="alert">
          <div class="bg-danger me-3 icon-item"><span class="fas fa-exclamation-circle text-white fs-6"></span></div>
          <p class="mb-0 flex-1">{{ submitError }}</p>
        </div>

        <div v-if="limitReached" class="alert alert-warning border-0 d-flex align-items-center" role="alert">
          <div class="bg-warning me-3 icon-item"><span class="fas fa-exclamation-triangle text-white fs-6"></span></div>
          <p class="mb-0 flex-1">{{ limitReachedMessage }}</p>
        </div>

        <form @submit.prevent="submitForm()" class="needs-validation" novalidate>

          <div class="row mb-3">
            <div class="col">
              <div class="form-floating">
                <input v-model="form.label" id="apiKeyLabel" class="form-control" type="text" placeholder="Label" maxlength="255">
                <label for="apiKeyLabel">Label</label>
                <div v-if="formValidations.labelError" class="invalid-feedback d-block fs-10">{{ formValidations.labelError }}</div>
              </div>
            </div>
          </div>

          <div class="row mb-3">
            <div class="col">
              <label class="fw-bold fs-10 text-uppercase text-700 mb-2">Permissions</label>

              <div class="form-check">
                <input class="form-check-input" type="checkbox" id="scope-read" checked disabled>
                <label class="form-check-label" for="scope-read"><span class="fw-bold">Read</span> - read your resources (always granted)</label>
              </div>
              <div class="form-check">
                <input v-model="form.scopes.write" class="form-check-input" type="checkbox" id="scope-write">
                <label class="form-check-label" for="scope-write"><span class="fw-bold">Write</span> - create and modify your bots and settings</label>
              </div>
              <div class="form-check">
                <input v-model="form.scopes.trading" class="form-check-input" type="checkbox" id="scope-trading">
                <label class="form-check-label" for="scope-trading"><span class="fw-bold">Trading</span> - execute trades on your bots</label>
              </div>
            </div>
          </div>

          <div class="row mt-4">
            <div class="col d-flex justify-content-end">
              <button v-if="!loading" :disabled="limitReached" @click="submitForm()" type="submit" class="btn btn-primary">
                <span class="fas fa-plus" data-fa-transform="shrink-3 down-2"></span>
                Create API Key
              </button>

              <div v-if="loading" class="spinner-border" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </form>
      </div>

    </div>
  </div>
</template>

<script>
// Per-user limits enforced by the API (components/api ApiKeyController:
// MAX_KEYS / MAX_ACTIVE_KEYS). Mirrored here only to give the user early,
// friendly feedback - the API remains the source of truth and returns 422
// if these are exceeded.
const MAX_KEYS = 25
const MAX_ACTIVE_KEYS = 10

export default {
  data () {
    return {
      loading: false,
      submitError: null,
      createdToken: null,
      createdLabel: null,
      copied: false,
      form: {
        label: '',
        scopes: {
          write: false,
          trading: false
        }
      },
      formValidations: {}
    }
  },
  computed: {
    apiKeys () {
      return this.$store.getters.getApiKeys || []
    },
    activeKeyCount () {
      return this.apiKeys.filter(k => k.active).length
    },
    limitReached () {
      return this.apiKeys.length >= MAX_KEYS || this.activeKeyCount >= MAX_ACTIVE_KEYS
    },
    limitReachedMessage () {
      if (this.apiKeys.length >= MAX_KEYS) {
        return `You have reached the maximum of ${MAX_KEYS} API keys. Delete an inactive key to create a new one.`
      }
      return `You have reached the maximum of ${MAX_ACTIVE_KEYS} active API keys. Deactivate a key to create a new one.`
    }
  },
  methods: {
    validateForm () {
      this.formValidations = {}

      if (!this.form.label || this.form.label.trim() === '') {
        this.formValidations.labelError = 'Enter a label so you can recognise this key later.'
        return false
      }

      return true
    },
    buildScopes () {
      const scopes = []
      if (this.form.scopes.write) scopes.push('write')
      if (this.form.scopes.trading) scopes.push('trading')
      return scopes
    },
    async submitForm () {
      this.submitError = null

      if (!this.validateForm()) return

      this.loading = true

      const payload = {
        label: this.form.label.trim(),
        scopes: this.buildScopes()
      }

      const result = await this.$store.dispatch('createApiKey', payload)

      this.loading = false

      if (result && result.ok) {
        this.createdToken = result.token
        this.createdLabel = result.apiKey?.label || payload.label
        this.resetForm()

        // Confetti to celebrate, same as after creating a bot.
        this.$confetti.start()
        setTimeout(() => { this.$confetti.stop() }, 2000)
      } else {
        this.submitError = (result && result.error) || 'Could not create the API key. Please try again.'
      }
    },
    resetForm () {
      this.form = {
        label: '',
        scopes: { write: false, trading: false }
      }
      this.formValidations = {}
    },
    dismissReveal () {
      this.createdToken = null
      this.createdLabel = null
      this.copied = false
    },
    async copyToken () {
      try {
        await navigator.clipboard.writeText(this.createdToken)
        this.copied = true
        setTimeout(() => { this.copied = false }, 2000)
      } catch (e) {
        // Clipboard API unavailable (e.g. non-HTTPS context) - the field is
        // selectable so the user can still copy manually.
        this.copied = false
      }
    }
  }
}
</script>
